import { useEffect, useState, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    FormGroup,
    Button,
    Input,
    InputGroup,
    UncontrolledTooltip,
} from "reactstrap";

import closeIcon from "Images/Close.svg";
import useIsAuthorized from "Hooks/useIsAuthorized";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Login/LoginCompany/LoginCompany.module.css";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import useCompanyLogos from "Hooks/useCompanyLogos";

export default function LoginCompany() {
    const [companies, setCompanies] = useState<ICompany[]>([]);
    const [query, setQuery] = useState("");

    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();

    useIsAuthorized();

    const { handleLoginCompanyOkResponse } = useHelpers();
    const { post } = useFetch();
    const { onImageError, getLogo } = useCompanyLogos();

    /**  Fetch companies for user to pick from */
    useEffect(() => {
        let ignore = false;
        // If user is logging in with jProcore and there is more than one company to pick from
        if (
            userContext.procoreLoginCompanies &&
            userContext.procoreLoginCompanies.length > 1
        ) {
            setCompanies(userContext.procoreLoginCompanies);
        } else {
            (async () => {
                // The backend identifies the user from the login cookie or the current session
                const response = await fetch(`/api/login_company`);
                const data = await response.json();
                console.log(data);

                if (response.ok && !ignore) {
                    setCompanies(data);
                }
            })();
        }
        return () => {
            ignore = true;
        };
    }, [
        userContext.isAuthenticated,
        userContext.isCalanceAdmin,
        userContext.procoreLoginCompanies,
        userContext.userId,
    ]);

    const loginCompany = useCallback(
        async (company_id: string) => {
            const url = `/api/login_company?company_id=${company_id}`;

            const response = await post(url);
            const data = await response.json();
            console.log("Login Company response (userContext): ", data);

            if (response.ok) {
                // Clear integration log filters from previous company
                Object.keys(localStorage)
                    .filter((k) => k.startsWith("runFilter."))
                    .forEach((k) => localStorage.removeItem(k));
                handleLoginCompanyOkResponse(data);
            } else {
                newAlert({
                    color: "danger",
                    message: data.detail,
                });
            }
        },
        [
            handleLoginCompanyOkResponse,
            newAlert,
            post,
        ]
    );

    /** If there's just one company, go ahead and automatically log in with it */
    useEffect(() => {
        if (companies.length === 1) {
            loginCompany(companies[0].company_id);
        }
    }, [companies, loginCompany]);

    /** Fires when user clicks on a company to log in with */

    /** Logs in if user hits enter when filters show only one company */
    function handleEnterKeyPress(e: React.FormEvent) {
        e.preventDefault();

        if (companiesShown.length === 1) {
            loginCompany(companiesShown[0].company_id);
        }
    }

    /** Filters companies shown based on search input */
    const companiesShown = companies.filter(
        (c: any) =>
            c.company_name.toLowerCase().includes(query.toLowerCase()) ||
            c.company_id.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <Container className={styles.main}>
            <Row>
                {/* Search companies box */}
                <Col lg="3" className={styles.flexCol}>
                    <div className={styles.formBox}>
                        <Form
                            className={styles.form}
                            onSubmit={handleEnterKeyPress}
                        >
                            <h2>Search Companies</h2>
                            <FormGroup>
                                <InputGroup>
                                    <Input
                                        list="companies"
                                        type="text"
                                        value={query}
                                        autoFocus
                                        onChange={(e) =>
                                            setQuery(e.target.value)
                                        }
                                    ></Input>
                                    <Button onClick={() => setQuery("")}>
                                        <img
                                            src={closeIcon}
                                            alt="Close"
                                            style={{
                                                height: "30px",
                                                width: "30px",
                                            }}
                                        />
                                    </Button>
                                </InputGroup>
                            </FormGroup>
                        </Form>
                    </div>
                </Col>
                {/* Companies buttons  */}
                <Col>
                    <div className={styles.logosBox}>
                        {companiesShown.map((c: ICompany, index: number) => (
                            <div key={index}>
                                <Button
                                    id={`button-${index}`}
                                    className={styles.logoButton}
                                    onClick={() => loginCompany(c.company_id)}
                                    style={{ animationDelay: `${index * 5}ms` }}
                                >
                                    <b>{c.company_name}</b>
                                    <img
                                        className={styles.logo}
                                        src={getLogo(c.company_id)}
                                        onError={(e) => onImageError(e)}
                                        alt="Logo"
                                    />
                                </Button>
                                <UncontrolledTooltip target={`button-${index}`}>
                                    {`${c.company_id}-${c.company_name}`}
                                </UncontrolledTooltip>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
        </Container>
    );
}
